import { Table, Button, Typography, Flex } from "antd"
import { PlusOutlined } from "@ant-design/icons"
import styles from "./index.module.css"
import AddNewFeeModal from "./modal/AddNewFee"
import PlatformFeesCard from "./components/PlatformFeesCard"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import DeleteConfirmation from "../ProSettings/CustomTemplate/modal/DeleteConfirmation"
import AssociatedExperiencesConfirmModal from "./modal/AssociatedExperiencesConfirmModal"
import { usePlatformFees } from "./hooks/usePlatformFees"
import { useCustomFees } from "./hooks/useCustomFees"
import { useCardProcessingFee } from "./hooks/useCardProcessingFee"
import { getColumns } from "./columns"
import DSTab from "ui/DSTab"
import { spacings } from "themes/variables"
import CardProcessingFeeCard from "./components/CardProcessingFeeCard"
import useActiveGuide from "hooks/useActiveClient"

const { Title, Text } = Typography

const TAB_KEYS = {
  CUSTOM_FEES: "custom_fees",
  PLATFORM_FEES: "platform_fees",
  CREDIT_CARD_FEE: "card_processing_fee"
}

export const TaxesAndFees = () => {
  const { t } = useTranslation()
  const { isActive: active, isPending } = useActiveGuide()

  const tabItems = [
    { key: TAB_KEYS.CUSTOM_FEES, label: t("settings.tab_custom_fees") },
    { key: TAB_KEYS.PLATFORM_FEES, label: t("settings.tab_platform_fees") },
    { key: TAB_KEYS.CREDIT_CARD_FEE, label: t("settings.tab_credit_card_fee") }
  ]
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get("tab") || TAB_KEYS.CUSTOM_FEES
  const setActiveTab = (key) => setSearchParams({ tab: key }, { replace: true })

  const {
    customFees,
    customFeesLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isMutating,
    isModalOpen,
    deleteModalOpen,
    feeToEdit,
    handleAddFee,
    handleDeleteConfirm,
    handleDeleteCancel,
    openAddModal,
    closeModal,
    getMenuItems,
    assocExpModalOpen,
    pendingAction,
    associatedExperiences,
    isCheckingAssociations,
    isConfirming,
    handleAssocExpConfirm,
    handleAssocExpCancel,
    activateModalOpen,
    handleActivateConfirm,
    handleActivateCancel,
    feeToActivate
  } = useCustomFees({ enabled: activeTab === TAB_KEYS.CUSTOM_FEES })
  
  const {
    feePayerMode,
    setFeePayerMode,
    splitType,
    setSplitType,
    guidePortion,
    setGuidePortion,
    guidePortionError,
    guestPortion,
    isLoading: platformFeesLoading,
    isSaving,
    handleSaveChanges
  } = usePlatformFees({ enabled: activeTab === TAB_KEYS.PLATFORM_FEES })

  const { passFeeToGuest, handleToggle, isLoading: cardFeeLoading, isSaving: cardFeeSaving } = useCardProcessingFee({ enabled: activeTab === TAB_KEYS.CREDIT_CARD_FEE })

  return (
    <>
      <DSTab items={tabItems} activeKey={activeTab} onChange={setActiveTab} className={styles.tabContainer} />

      {activeTab === TAB_KEYS.CUSTOM_FEES && (
        <div className={styles.container}>
          <div className={styles.headerRow}>
            <div className={styles.titleWithInfo}>
              <Title level={4}>{t("settings.add_custom_fees")}</Title>
            </div>
            <Button color="primary" variant="solid" icon={<PlusOutlined />} onClick={active && !isPending ? openAddModal : undefined} disabled={!active || isPending} aria-disabled={!active || isPending}>
              {t("settings.add_new")}
            </Button>
          </div>
          <Text className={styles.description}>{t("settings.custom_fees_desc")}</Text>
          <div className={styles.tableWrapper}>
            <Table
              columns={getColumns(getMenuItems, isMutating, t)}
              dataSource={customFeesLoading ? undefined : customFees}
              rowKey="id"
              loading={customFeesLoading}
              pagination={false}
              locale={customFeesLoading ? { emptyText: <></> } : undefined}
            />
          </div>
        </div>
      )}

      {activeTab === TAB_KEYS.CREDIT_CARD_FEE && (
        <CardProcessingFeeCard
          passFeeToGuest={passFeeToGuest}
          onToggle={handleToggle}
          isLoading={cardFeeLoading}
          isSaving={cardFeeSaving}
        />
      )}

      {activeTab === TAB_KEYS.PLATFORM_FEES && (
        <>
          <PlatformFeesCard
            feePayerMode={feePayerMode}
            setFeePayerMode={setFeePayerMode}
            splitType={splitType}
            setSplitType={setSplitType}
            guidePortion={guidePortion}
            setGuidePortion={setGuidePortion}
            guidePortionError={guidePortionError}
            guestPortion={guestPortion}
            isLoading={platformFeesLoading}
          />
          <Flex justify="end" gap={spacings.S} className="mb-xl mt-l">
            <Button onClick={() => navigate(-1)} color="primary" variant="outlined">
              {t("common.cancel")}
            </Button>
            <Button color="primary" variant="solid" onClick={handleSaveChanges} loading={isSaving}>
              {t("settings.save_changes")}
            </Button>
          </Flex>
        </>
      )}

      <AddNewFeeModal
        visible={isModalOpen}
        onCancel={closeModal}
        onAdd={handleAddFee}
        initialValues={feeToEdit}
        isSaving={isCreating || isUpdating}
      />
      <DeleteConfirmation
        visible={deleteModalOpen}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("settings.delete_custom_fee_title")}
        loading={isDeleting}
        feeTitle={pendingAction?.feeTitle || ""}
      />
      <DeleteConfirmation
        visible={activateModalOpen}
        onCancel={handleActivateCancel}
        onConfirm={handleActivateConfirm}
        title={t("settings.activate_custom_fee_title")}
        confirmText={t("settings.action_activate")}
        loading={isUpdating}
        feeTitle={feeToActivate?.feeTitle || feeToActivate?.title || ""}
      />
      <AssociatedExperiencesConfirmModal
        visible={assocExpModalOpen}
        onCancel={handleAssocExpCancel}
        onConfirm={handleAssocExpConfirm}
        actionType={pendingAction?.type}
        experiences={associatedExperiences}
        isCheckingAssociations={isCheckingAssociations}
        isConfirming={isConfirming}
        customFeeTitle={pendingAction?.feeTitle || ""}
      />
    </>
  )
}
